// $FlowFixMe
import React, { useState } from 'react';
import { connect } from 'react-redux';
import { injectIntl } from 'react-intl';
import { Button } from 'react-bootstrap';
import { bindActionCreators } from 'redux';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { Spinner } from '@fortress-technology-solutions/fortress-component-library/Atoms';

import confirm from '../../../../../../components/ConfirmDialogModal';
import ElementWithPermissions from '../../../../../../components/ElementWithPermissions';
import ConditionalTooltip from '../../../../../../components/ConditionalTooltip';

import { editLease } from '../../../../actions';

import UploadLeaseModal from '../ButtonActionModals/UploadLeaseModal';
import messages from './messages';
import { useGateTooltip } from './hooks';

type ManualMethodButtonsProps = {
  allAffordableTabsApproved: boolean,
  applicationId: string,
  canComplete: boolean,
  difference: number,
  isEditLeaseMode?: boolean,
  isResident: boolean,
  leaseId: string,
  onGenerate?: Function,
  setIsEditLeaseMode: Function,
  profileId: string,
  transferCertBlocked?: boolean,
  transferCertTooltip?: string,
  unapprovedAffordablePrograms?: Array<string>,
  moveInCertBlocked?: boolean,
  moveInCertTooltip?: string,
  providerUnknown?: boolean,
  showGenerateInBlueMoon?: boolean,
  providerIsUsable?: boolean,
  onGenerateInBlueMoon?: Function,
};

type ManualMethodButtonsInjectedProps = {
  actions: Object,
  intl: Object,
};

export const ManualMethodButtons = ({
  actions,
  allAffordableTabsApproved,
  applicationId,
  canComplete,
  difference,
  intl,
  isEditLeaseMode = false,
  isResident,
  leaseId,
  onGenerate,
  profileId,
  setIsEditLeaseMode,
  selectedProperty,
  transferCertBlocked = false,
  transferCertTooltip = '',
  unapprovedAffordablePrograms = [],
  moveInCertBlocked = false,
  moveInCertTooltip = '',
  providerUnknown = false,
  showGenerateInBlueMoon = false,
  providerIsUsable = true,
  onGenerateInBlueMoon,
}: ManualMethodButtonsProps & ManualMethodButtonsInjectedProps) => {
  const [showUploadModal, setShowUploadModal] = useState(false);

  const handleShowUploadClick = () => setShowUploadModal(true);
  const handleHideUploadClick = () => setShowUploadModal(false);

  const confirmEditDialog = () => {
    confirm(intl.formatMessage(messages.confirmEditLease)).then(() => {
      actions.editLease(
        leaseId,
        profileId,
        isResident,
        undefined,
        undefined,
        undefined,
        setIsEditLeaseMode,
      );
    });
  };

  const { automateMonthlyLeaseTransactionsMvp: automateMLTsFlag } = useFlags();
  const notReconciled = automateMLTsFlag ? false : difference !== 0;

  const { certGateTooltip, showCertGateTooltip, gateTooltip, showGateTooltip } =
    useGateTooltip({
      intl,
      allAffordableTabsApproved,
      unapprovedAffordablePrograms,
      transferCertBlocked,
      transferCertTooltip,
      moveInCertBlocked,
      moveInCertTooltip,
      providerUnknown,
    });
  // A missing handler comes last. ENG-33905 supplies the handler.
  let blueMoonTooltip = certGateTooltip;
  if (!providerIsUsable) {
    blueMoonTooltip = intl.formatMessage(messages.blueMoonUnavailable);
  } else if (!showCertGateTooltip && !onGenerateInBlueMoon) {
    blueMoonTooltip = intl.formatMessage(messages.blueMoonGeneratePending);
  }
  const showBlueMoonTooltip =
    !providerIsUsable || showCertGateTooltip || !onGenerateInBlueMoon;
  // A Blue Moon lease never downloads a Fortress PDF: the backend refuses the generator with a
  // 409. Download Lease waits for the Blue Moon packet, which ENG-33905 supplies.
  const downloadTooltip = showGenerateInBlueMoon
    ? intl.formatMessage(messages.blueMoonDownloadPending)
    : gateTooltip;
  const showDownloadTooltip = showGenerateInBlueMoon || showGateTooltip;

  return (
    <>
      <UploadLeaseModal
        applicationId={applicationId}
        isResident={isResident}
        leaseId={leaseId}
        onHide={handleHideUploadClick}
        profileId={profileId}
        show={showUploadModal}
      />
      <ElementWithPermissions scope={['lease-create']}>
        <Button
          className="btn btn-tertiary"
          onClick={confirmEditDialog}
          disabled={isEditLeaseMode}
          data-testid={'editButton'}
        >
          {isEditLeaseMode ? (
            <Spinner small />
          ) : (
            intl.formatMessage(messages.edit)
          )}
        </Button>
        {showGenerateInBlueMoon && !isEditLeaseMode && (
          <ConditionalTooltip
            containerSX={{
              display: 'inline-block',
              // Same spacing carry-over as the gated buttons below.
              marginRight: '3%',
              '&& .btn': { marginRight: 0 },
            }}
            show={showBlueMoonTooltip}
            title={blueMoonTooltip}
            placement={'top'}
            variant={'light'}
          >
            {/* span wrapper: a disabled MUI button swallows pointer events. */}
            <ElementWithPermissions scope={['lease-create']}>
              <Button
                className="btn btn-primary"
                onClick={onGenerateInBlueMoon}
                disabled={
                  !providerIsUsable ||
                  !onGenerateInBlueMoon ||
                  !canComplete ||
                  notReconciled ||
                  !allAffordableTabsApproved ||
                  moveInCertBlocked ||
                  transferCertBlocked
                }
                data-testid={'generateInBlueMoonButton'}
              >
                {intl.formatMessage(messages.generateInBlueMoon)}
              </Button>
            </ElementWithPermissions>
          </ConditionalTooltip>
        )}
        {!isEditLeaseMode && (
          <ConditionalTooltip
            containerSX={{
              display: 'inline-block',
              // `.form-footer .btn`'s `margin-right: 3%` is a percentage, so it resolves
              // against the button's containing block. This shrink-to-fit container becomes
              // that block, collapsing the gap to ~0 and leaving the gated buttons flush
              // against each other. Carry the spacing here — where 3% still resolves against
              // `.button-group` — and neutralise it on the button. `&&` outweighs
              // `.form-footer .btn` without needing `!important`.
              marginRight: '3%',
              '&& .btn': { marginRight: 0 },
            }}
            show={showGateTooltip}
            title={gateTooltip}
            placement={'top'}
            variant={'light'}
          >
            {/* span wrapper: a disabled MUI button swallows pointer events. ElementWithPermissions
                goes inside the span (not the outer wrapper above) so this Button stays its direct
                cloned child and keeps receiving the permission-driven `disabled` — wrapping it in
                ConditionalTooltip first would otherwise swallow that clone (see
                UnselectedMethodSaveButton.js). */}
            <ElementWithPermissions scope={['lease-create']}>
              <Button
                className="btn btn-shout"
                onClick={handleShowUploadClick}
                disabled={
                  !canComplete ||
                  notReconciled ||
                  !allAffordableTabsApproved ||
                  moveInCertBlocked ||
                  transferCertBlocked ||
                  providerUnknown
                }
                data-testid={'uploadSignedLeaseButton'}
              >
                {intl.formatMessage(messages.uploadSignedLease)}
              </Button>
            </ElementWithPermissions>
          </ConditionalTooltip>
        )}
        {onGenerate && !isEditLeaseMode && (
          <ConditionalTooltip
            containerSX={{
              display: 'inline-block',
              // `.form-footer .btn`'s `margin-right: 3%` is a percentage, so it resolves
              // against the button's containing block. This shrink-to-fit container becomes
              // that block, collapsing the gap to ~0 and leaving the gated buttons flush
              // against each other. Carry the spacing here — where 3% still resolves against
              // `.button-group` — and neutralise it on the button. `&&` outweighs
              // `.form-footer .btn` without needing `!important`.
              marginRight: '3%',
              '&& .btn': { marginRight: 0 },
            }}
            show={showDownloadTooltip}
            title={downloadTooltip}
            placement={'top'}
            variant={'light'}
          >
            {/* span wrapper: a disabled MUI button swallows pointer events. ElementWithPermissions
                goes inside the span for the same reason as Upload Signed Lease above. */}
            <ElementWithPermissions scope={['lease-create']}>
              <Button
                className="btn btn-primary"
                disabled={
                  showGenerateInBlueMoon ||
                  !canComplete ||
                  notReconciled ||
                  !allAffordableTabsApproved ||
                  moveInCertBlocked ||
                  transferCertBlocked ||
                  providerUnknown
                }
                onClick={onGenerate}
                data-testid={'downloadLeaseButton'}
              >
                {intl.formatMessage(messages.downloadLease)}
              </Button>
            </ElementWithPermissions>
          </ConditionalTooltip>
        )}
      </ElementWithPermissions>
      {notReconciled && (
        <div className="text-center" style={{ marginRight: '3%' }}>
          {intl.formatMessage(messages.notReconciled)}
        </div>
      )}
    </>
  );
};

export function mapDispatchToProps(dispatch: any): Object {
  const actions = bindActionCreators({ editLease }, dispatch);
  return { actions };
}

const InjectedManualMethodButtons = injectIntl(ManualMethodButtons);

export default connect(null, mapDispatchToProps)(InjectedManualMethodButtons);
